import React from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

import StatusSnackbar from 'components/common/StatusSnackbar';
import useStatusSnackbar from 'hooks/useStatusSnackbar';
import { useMoodleSsoLoginMutation } from 'services/api/moodle.api';
import { extractErrorMessage } from 'utils/rbac/errorHelpers';

import { StyledNavItemButton } from './Sidebar.styles';
import { NavItem } from './Sidebar.types';

interface MoodleCoursesNavItemProps {
  item: NavItem;
  isCollapsed: boolean;
  onClose?: () => void;
}

const MoodleCoursesNavItem: React.FC<MoodleCoursesNavItemProps> = ({ item, isCollapsed, onClose }) => {
  const IconComponent = item.Icon;
  const iconProps = { className: 'sidebar-icon', style: { width: '20px', height: '20px' } };
  const [moodleSsoLogin, { isLoading }] = useMoodleSsoLoginMutation();
  const { snackbar, showSnackbar, hideSnackbar } = useStatusSnackbar();

  const handleClick = async () => {
    if (isLoading) return;

    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      showSnackbar('Error', 'El navegador bloqueó la ventana de Moodle', 'error');
      return;
    }
    tab.opener = null;

    try {
      const { loginUrl } = await moodleSsoLogin().unwrap();
      tab.location.replace(loginUrl);
      onClose?.();
    } catch (error: unknown) {
      tab.close();
      showSnackbar('Error', extractErrorMessage(error, 'No se pudo abrir Moodle'), 'error');
    }
  };

  const content = (
    <StyledNavItemButton
      disabled={isLoading}
      onClick={() => {
        void handleClick();
      }}
      sx={{
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        px: isCollapsed ? 1 : 1.5,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: isCollapsed ? 0 : 40,
          color: 'inherit',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isLoading ? <CircularProgress size={18} color="inherit" /> : <IconComponent {...iconProps} />}
      </ListItemIcon>
      {!isCollapsed && (
        <ListItemText
          primary={item.text}
          sx={(theme) => ({
            flex: 1,
            '& .MuiListItemText-primary': {
              fontSize: '16px',
              fontWeight: 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              [theme.breakpoints.up('xl')]: {
                fontSize: '18px',
              },
            },
          })}
        />
      )}
    </StyledNavItemButton>
  );

  return (
    <>
      {isCollapsed ? (
        <Tooltip title={item.text} placement="right" arrow>
          <ListItem disablePadding sx={{ py: 0.5 }}>
            {content}
          </ListItem>
        </Tooltip>
      ) : (
        <ListItem disablePadding sx={{ py: 0.5 }}>
          {content}
        </ListItem>
      )}
      <StatusSnackbar
        open={snackbar.open}
        title={snackbar.title}
        message={snackbar.message}
        variant={snackbar.variant}
        onClose={hideSnackbar}
      />
    </>
  );
};

export default MoodleCoursesNavItem;
